import { useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  ClockIcon,
  ShieldCheckIcon,
  SpeakerWaveIcon,
  SparklesIcon,
  UserGroupIcon,
  WifiIcon,
} from '@heroicons/react/24/outline';

/**
 * Full-page waiting room shown before the live AI interview while every
 * interviewer is busy, or while the connection is being retried. Same visual
 * language as the screening/apply pages (white-smoke page, TalentCrux header,
 * rounded card with an electric-blue header strip).
 *
 * @param {object} props
 * @param {'waiting'|'connecting'|'failed'} props.mode
 * @param {number|null} [props.position] place in line, 1 = next
 * @param {{low:number, high:number}|null} [props.estimatedWaitMinutes]
 * @param {string} [props.message] shown when mode === 'failed'
 * @param {() => void} [props.onRetry]
 * @param {() => void} [props.onBack]
 * @param {(key: string, vars?: object) => string} props.t candidate translator
 */
export default function InterviewWaitingLobby({
  mode,
  position = null,
  estimatedWaitMinutes = null,
  message,
  onRetry,
  onBack,
  t,
}) {
  const elapsed = useElapsedSeconds(mode !== 'failed');

  const header = (
    <header className="flex items-center justify-between gap-3">
      <div className="flex flex-col min-w-0">
        <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          {t('Powered by')}
        </p>
        <div className="mt-0.5 flex items-center gap-1.5">
          <img
            src="/logo.svg"
            alt="TalentCrux"
            className="h-9 w-9 sm:h-12 sm:w-12 md:h-14 md:w-14 object-contain flex-shrink-0"
          />
          <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate">
            TalentCrux
          </span>
        </div>
      </div>
    </header>
  );

  const copy = {
    waiting: {
      strip: t("You're in the waiting room"),
      title: t('Your interviewer will be with you shortly'),
      body: t(
        "All our AI interviewers are talking with other candidates right now. You're in line, and your interview will start automatically as soon as one is free."
      ),
    },
    connecting: {
      strip: t('Getting your interview ready'),
      title: t('Connecting you to your interviewer…'),
      body: t('This is taking a little longer than usual. We are retrying automatically — no need to do anything.'),
    },
    failed: {
      strip: t('Getting your interview ready'),
      title: t("We couldn't connect you just now"),
      body: message ? t(message) : '',
    },
  }[mode];

  return (
    <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
      <div className="mx-auto max-w-6xl">{header}</div>

      <main className="mx-auto mt-6 sm:mt-10 max-w-xl" aria-live="polite">
        <div className="bg-white rounded-3xl border border-gray-200 shadow-md overflow-hidden">
          <div className="bg-electric-blue px-5 py-4 sm:px-6 sm:py-5 flex items-center justify-between gap-3">
            <h1 className="text-base sm:text-lg font-bold text-white">{copy.strip}</h1>
            {mode !== 'failed' && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-[10px] sm:text-xs font-semibold text-electric-blue-700 shadow-sm whitespace-nowrap">
                <ClockIcon className="h-4 w-4 text-electric-blue-500" aria-hidden />
                {formatElapsed(elapsed)}
              </span>
            )}
          </div>

          <div className="p-5 sm:p-7 text-center">
            <LobbyBeacon mode={mode} />

            <h2 className="mt-5 text-lg sm:text-xl font-bold text-gray-900">{copy.title}</h2>
            {copy.body && (
              <p className="mt-2 text-sm text-gray-600 leading-relaxed max-w-md mx-auto">{copy.body}</p>
            )}

            {mode === 'waiting' && (position || estimatedWaitMinutes) && (
              <div className="mt-6 grid grid-cols-2 gap-3">
                <StatTile
                  icon={UserGroupIcon}
                  label={t('Your place in line')}
                  value={position ? (position === 1 ? t("You're next") : `#${position}`) : '—'}
                />
                <StatTile
                  icon={ClockIcon}
                  label={t('Estimated wait')}
                  value={
                    estimatedWaitMinutes
                      ? t('About {low}–{high} min', estimatedWaitMinutes)
                      : '—'
                  }
                />
              </div>
            )}

            {mode === 'failed' ? (
              <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-center">
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    className="px-5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    {t('Go Back')}
                  </button>
                )}
                {onRetry && (
                  <button
                    type="button"
                    onClick={onRetry}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-electric-blue to-indigo-600 text-sm font-bold text-white shadow-lg shadow-electric-blue/20 hover:from-electric-blue/90 hover:to-indigo-700 transition-colors"
                  >
                    <ArrowPathIcon className="h-4 w-4" aria-hidden />
                    {t('Try again')}
                  </button>
                )}
              </div>
            ) : (
              <p className="mt-5 inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-electric-blue">
                <span className="relative flex h-2 w-2" aria-hidden>
                  <span className="absolute inline-flex h-full w-full rounded-full bg-electric-blue opacity-60 motion-safe:animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-electric-blue" />
                </span>
                {mode === 'waiting'
                  ? t('Checking for a free interviewer…')
                  : t('Reconnecting…')}
              </p>
            )}
          </div>

          {mode === 'waiting' && (
            <div className="border-t border-gray-100 bg-gray-50/60 px-5 py-4 sm:px-7 sm:py-5">
              <ul className="space-y-3 text-left">
                <Tip
                  icon={WifiIcon}
                  title={t('Keep this tab open')}
                  text={t('You keep your place in line while this page stays open.')}
                />
                <Tip
                  icon={SpeakerWaveIcon}
                  title={t('Stay somewhere quiet')}
                  text={t('Your interview starts the moment an interviewer is free.')}
                />
                <Tip
                  icon={ShieldCheckIcon}
                  title={t("Waiting doesn't count")}
                  text={t('Time in the waiting room is not part of your interview.')}
                />
              </ul>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function LobbyBeacon({ mode }) {
  const failed = mode === 'failed';
  const Icon = failed ? WifiIcon : SparklesIcon;
  return (
    <div className="relative mx-auto h-20 w-20 sm:h-24 sm:w-24" aria-hidden>
      {!failed && (
        <>
          <span className="absolute inset-0 rounded-full bg-electric-blue/10 motion-safe:animate-ping [animation-duration:2.4s]" />
          <span className="absolute inset-2 rounded-full bg-electric-blue/10 motion-safe:animate-pulse" />
        </>
      )}
      <span
        className={`absolute inset-3 sm:inset-4 flex items-center justify-center rounded-full ${
          failed
            ? 'bg-amber-50 border-2 border-amber-200'
            : 'bg-gradient-to-br from-electric-blue to-indigo-600 shadow-lg shadow-electric-blue/30'
        }`}
      >
        <Icon className={`h-7 w-7 sm:h-8 sm:w-8 ${failed ? 'text-amber-500' : 'text-white'}`} />
      </span>
    </div>
  );
}

function StatTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-3 py-3 sm:px-4 sm:py-4 text-left">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-electric-blue-50">
          <Icon className="h-4 w-4 text-electric-blue" aria-hidden />
        </span>
        <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </span>
      </div>
      <p className="mt-2 text-base sm:text-lg font-bold text-gray-900">{value}</p>
    </div>
  );
}

function Tip({ icon: Icon, title, text }) {
  return (
    <li className="flex gap-3">
      <span className="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-electric-blue-50">
        <Icon className="h-4 w-4 text-electric-blue" aria-hidden />
      </span>
      <div>
        <p className="text-xs sm:text-sm font-bold text-gray-900">{title}</p>
        <p className="text-xs sm:text-sm text-gray-600">{text}</p>
      </div>
    </li>
  );
}

function useElapsedSeconds(running) {
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);
  return Math.max(0, Math.floor((now - startedAt) / 1000));
}

function formatElapsed(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
